import axios from 'axios';
import Swal from 'sweetalert2';
import snackBarService from '@/shared/snackbar/snackbar.service';
import WorkItem from '@/domain/work-item/work-item.entity';
import WorkItemFilter from '@/domain/work-item/work-item-filter.entity';
import WorkItemDetailed from '@/domain/work-item/work-item-detailed.entity';
import WorkItemSummary from '@/domain/work-item/work-item-summary.entity';
import { processErrorMessage } from '@/shared/exceptions/error.helper';
import WorkItemRelated from './work-item-related.entity';
import WorkItemLatestTimestamp from './work-item-latest-timestamp.entity';
import WorkItemExport from './work-item-export.entity';


export default class WorkItemService {
  static saveWorkItem(projectId: string, workitem: any) {
    if (workitem.id) {
      return this.updateWorkitem(projectId, workitem.id, workitem);
    }
    return this.createWorkitem(projectId, workitem);
  }

  static getOneWorkItemById(projectId: string, workItemId: string) {
    return axios
      .get(`/workitems/${projectId}/${workItemId}`)
      .then((response: any) => WorkItemDetailed.of(response.data))
      .catch((err: any) => snackBarService.errorMessage(processErrorMessage(err)));
  }

  static searchAllWorkItems(projectId: string, filter: WorkItemFilter, query: string)
  : Promise<WorkItemSummary[]> {
    return axios
      .post(`/workitems/${projectId}/filter${query}`, filter)
      .then((response: any) => response.data)
      .catch((err: any) => snackBarService.errorMessage(processErrorMessage(err)));
  }

  static searchAllWorkItemsToExport(projectId: string,
    exportPreference: string,
    filter: WorkItemFilter): Promise<WorkItemExport[]> {
    return axios
      .post(`/workitems/${projectId}/${exportPreference}/filter`, filter)
      .then((response: any) => response.data)
      .catch((err: any) => snackBarService.errorMessage(processErrorMessage(err)));
  }

  static searchAllWorkItemsBySelectMember(projectId: string,
    filter: WorkItemFilter,
    selectMember: string,
    query: string)
  : Promise<WorkItemSummary[]> {
    return axios
      .post(`/workitems/${projectId}/filter${query}`, filter)
      .then((response: any) => response.data)
      .catch((err: any) => snackBarService.errorMessage(processErrorMessage(err)));
  }

  static searchAllWorkItemsUnpaged(
    projectId: string,
    parentWorkItemId: string,
  ): Promise<WorkItemSummary[]> {
    return axios.get(`/workitems/${projectId}/parent/${parentWorkItemId}`)
      .then((response: any) => response.data)
      .catch((err: any) => snackBarService.errorMessage(processErrorMessage(err)));
  }

  static searchAllAggregatorWorkItems(
    deliveryDateFrom?: string,
    deliveryDateTo?: string,
    projectId?: string,
    projectControlName?: string,
    assignedToIds?: string[],
  ): Promise<WorkItemSummary[]> {
    return axios
      .post(
        '/workitems/aggregators',
        {
          usersIds: assignedToIds === undefined ? [] : assignedToIds,
          deliveryDateFrom,
          deliveryDateTo,
          projectId,
          projectControlName,
        },
      )
      .then((response: any) => response.data)
      .catch((err: any) => snackBarService.errorMessage(processErrorMessage(err)));
  }

  static searchParentWorkItemsByProject(projectId: string): Promise<WorkItemRelated[]> {
    return axios
      .get(`/workitems/${projectId}/aggregators`)
      .then((response: any) => WorkItemRelated.ofList(response.data))
      .catch((err: any) => snackBarService.errorMessage(err.response.data.messages));
  }

  static createWorkitem(projectId: string, workitem: WorkItem) {
    return axios
      .post(`/workitems/project/${projectId}`, workitem)
      .then((response: any) => {
        snackBarService.successMessage('workItemSaved');
        return response;
      }).catch((err: any) => snackBarService.errorMessage(processErrorMessage(err)));
  }

  static updateWorkitem(projectId: string, workitemId: string, workitem: WorkItem) {
    return axios
      .put(`/workitems/${projectId}/${workitemId}`, workitem)
      .then((response: any) => {
        snackBarService.successMessage('workItemSaved');
        return response;
      }).catch((err: any) => {
        Swal.fire(
          'Erro',
          processErrorMessage(err),
          'error',
        );
      });
  }

  static reprocessingWorkItems(workitemIdList: string[]) {
    return axios
      .put('/workitems/reprocess', workitemIdList);
  }

  static deleteWorkitem(projectId: string, workitemId: string) {
    return axios.delete(`/workitems/${projectId}/${workitemId}`)
      .then(() => snackBarService.successMessage('workItemDeleted'))
      .catch((err: any) => snackBarService.errorMessage(processErrorMessage(err)));
  }

  static closeWorkitem(projectId: string, workitemId: string, workitem: WorkItem) {
    return axios.put(`/workitems/close/${projectId}/${workitemId}`, workitem)
      .then(() => snackBarService.successMessage('workItemClosed'))
      .catch((err: any) => snackBarService.errorMessage(processErrorMessage(err)));
  }

  static closeTaskWorkitem(projectId: string, workitemId: string) {
    return axios.put(`/workitems/close/${projectId}/${workitemId}`)
      .then(() => snackBarService.successMessage('workItemClosed'))
      .catch((err: any) => snackBarService.errorMessage(processErrorMessage(err)));
  }

  static getWorkItemErrors(projectId: string, workitemId: string) {
    return axios.get(`/events/project/${projectId}/workitem/${workitemId}`);
  }

  static getWorkItemErrorsByTitle(projectId: string, title: string) {
    return axios.get(`/events/project/${projectId}/workitem/title/${title}`);
  }

  static deleteWorkItemsError(projectId: string, idList: string[]) {
    return axios
      .delete(`/events/project/${projectId}/workitem`, { data: idList })
      .catch((err: any) => snackBarService.errorMessage(processErrorMessage(err)));
  }

  static getLatestTimetrack(workItemId: string): Promise<WorkItemLatestTimestamp> {
    return axios.get(`/workitems/${workItemId}/latest-timestamp`).then(({ data }: any) => data);
  }

  static getChildrenTasks(parentWorkItemId: string): Promise<WorkItem[]> {
    return axios
      .get(`/workitems/${parentWorkItemId}/children`)
      .then(({ data }: any) => data)
      .catch((err: any) => {
        snackBarService.errorMessage(processErrorMessage(err));
        throw err;
      });
  }

  static findDefaultTaskType(projectId: string, aggregatorTypeId: string): Promise<string | null> {
    return axios.get('/workitems/workitem-default-type/find', {
      params: { projectId, aggregatorTypeId },
    }).then(({ data }: any) => data.taskDefaultTypeId);
  }
}
